import {
  answerFeedbackSchema,
  checkoutSchema,
  childProfileSchema,
  familyBillingSchema,
  familySchema,
  hintSchema,
  playOverviewSchema,
  roundViewSchema,
  subjectPlanSchema,
} from '@oefy/contracts';
import type {
  AnswerInput,
  CancelSubscriptionInput,
  ChildInput,
  HintInput,
  QuestionReportInput,
  StartRoundInput,
} from '@oefy/contracts';
import type { SchoolGroup } from '@oefy/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, api } from '../lib/api';

export const playKeys = {
  family: ['family'] as const,
  billing: ['family', 'subscription'] as const,
  overview: (childId: string, schoolGroup: SchoolGroup) =>
    ['play', 'overview', childId, schoolGroup] as const,
  plan: (childId: string, subjectKey: string, schoolGroup: SchoolGroup) =>
    ['play', 'plan', childId, subjectKey, schoolGroup] as const,
  round: (roundId: string) => ['play', 'round', roundId] as const,
};

export function useFamily(enabled = true) {
  return useQuery({
    queryKey: playKeys.family,
    queryFn: () => api.get('/family', familySchema),
    enabled,
  });
}

export function useOverview(childId: string, schoolGroup: SchoolGroup | undefined) {
  return useQuery({
    queryKey: playKeys.overview(childId, schoolGroup ?? 5),
    queryFn: () =>
      api.get(
        `/children/${childId}/overview?schoolGroup=${String(schoolGroup)}`,
        playOverviewSchema,
      ),
    enabled: schoolGroup !== undefined,
  });
}

export function useSubjectPlan(
  childId: string,
  subjectKey: string,
  schoolGroup: SchoolGroup | undefined,
) {
  return useQuery({
    queryKey: playKeys.plan(childId, subjectKey, schoolGroup ?? 5),
    queryFn: () =>
      api.get(
        `/children/${childId}/subjects/${encodeURIComponent(subjectKey)}?schoolGroup=${String(schoolGroup)}`,
        subjectPlanSchema,
      ),
    enabled: schoolGroup !== undefined,
  });
}

export function useRound(roundId: string) {
  return useQuery({
    queryKey: playKeys.round(roundId),
    queryFn: () => api.get(`/rounds/${roundId}`, roundViewSchema),
    staleTime: 0,
  });
}

export function useStartRound(childId: string) {
  return useMutation({
    mutationFn: (input: StartRoundInput) =>
      api.post(`/children/${childId}/rounds`, input, z.object({ roundId: z.uuid() })),
  });
}

export function useAnswer(roundId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AnswerInput) =>
      api.post(`/rounds/${roundId}/answers`, input, answerFeedbackSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['play', 'overview'] });
      await queryClient.invalidateQueries({ queryKey: ['play', 'plan'] });
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === 'conflict') {
        await queryClient.invalidateQueries({ queryKey: playKeys.round(roundId) });
      }
    },
  });
}

export function useHint(roundId: string) {
  return useMutation({
    mutationFn: (input: HintInput) => api.post(`/rounds/${roundId}/hints`, input, hintSchema),
  });
}

export function useReportQuestion(roundId: string) {
  return useMutation({
    mutationFn: (input: QuestionReportInput) =>
      api.command('POST', `/rounds/${roundId}/reports`, input),
  });
}

const PAYMENT_POLL_MS = 3_000;
const PAYMENT_POLL_LIMIT = 20;

export function useFamilyBilling(waitingForPayment: boolean) {
  return useQuery({
    queryKey: playKeys.billing,
    queryFn: () => api.get('/family/subscription', familyBillingSchema),
    refetchInterval: (query) =>
      waitingForPayment &&
      (query.state.data?.subscriptions.some((subscription) => subscription.canPay) ?? true) &&
      query.state.dataUpdateCount < PAYMENT_POLL_LIMIT
        ? PAYMENT_POLL_MS
        : false,
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      subscriptionId,
      feedback,
    }: {
      readonly subscriptionId: string;
      readonly feedback: CancelSubscriptionInput;
    }) => api.post(`/family/subscriptions/${subscriptionId}/cancel`, feedback, familyBillingSchema),
    onSuccess: async (billing) => {
      queryClient.setQueryData(playKeys.billing, billing);
      await queryClient.invalidateQueries({ queryKey: playKeys.family, exact: true });
    },
  });
}

export function useCheckout() {
  return useMutation({
    mutationFn: (subscriptionId: string) =>
      api.post(`/family/subscriptions/${subscriptionId}/checkout`, undefined, checkoutSchema),
  });
}

export function useSchoolYear() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, movedUp }: { childId: string; movedUp: boolean }) =>
      api.post(`/family/children/${childId}/school-year`, { movedUp }, childProfileSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: playKeys.family });
      await queryClient.invalidateQueries({ queryKey: ['play', 'overview'] });
    },
  });
}

export function useAddChild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ChildInput) => api.post('/family/children', input, childProfileSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: playKeys.family }),
  });
}

export function useUpdateChild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: ChildInput & { id: string }) =>
      api.put(`/family/children/${id}`, input, childProfileSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: playKeys.family }),
  });
}

export function useChildPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, photo }: { childId: string; photo: Blob | null }) =>
      photo
        ? api.upload(`/family/children/${childId}/photo`, photo, childProfileSchema, 'PUT')
        : api.remove(`/family/children/${childId}/photo`, childProfileSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: playKeys.family }),
  });
}

export function useRemoveChild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.command('DELETE', `/family/children/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: playKeys.family }),
  });
}

export function useTextScale(childId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (textScale: number) =>
      api.put(`/family/children/${childId}/preferences`, { textScale }, childProfileSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: playKeys.family }),
  });
}
